const EVENTS = {
  ACCORDION_OPEN: 'accordion:open',
  ACCORDION_CLOSED: 'accordion:closed',
}

class AccordionDrawer extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-accordion-id']
  }

  static get requiredElements () {
    return ['panel']
  }

  connectedCallback () {
    super.connectedCallback()

    this.setPanelHeight(this.isOpen ? this.getExpandedHeight() : 0)

    if (this.isOpen) {
      // Because a lot of the CSS on pages are async loaded if an accordion is open by default it can get a false height until the CSS has been loaded properly. This will detect for any resizes and then redo the height
      const parent = this.closest('[data-accordion-drawer-parent]')
      if (!parent) return

      const resizeObserver = new ResizeObserver((entries, observer) => {
        this.setPanelHeight(this.isOpen ? this.getExpandedHeight() : 0)

        window.setTimeout(() => observer.unobserve(parent), 3000)
      })
      resizeObserver.observe(parent)
    }
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.toggle) {
      this.els.toggle.elements.forEach(toggle => {
        this.listeners.add(toggle, 'click', () => this.handleToggleClick())
      })
    }

    this.listeners.add(document, 'BVFilters', () =>
      this.setPanelHeight(this.getExpandedHeight()),
    )

    this.listeners.add(window, 'load', () => this.handleResize())

    this.listeners.add(window, 'resize', () => this.handleResize())
  }

  handleResize () {
    if (this.isOpen) {
      this.setPanelHeight(this.getExpandedHeight())
    }
  }

  getExpandedHeight () {
    const panel = this.els.panel?.element
    if (!panel) return 0

    const heightProperty = 'height'

    const currentHeight = panel.style.getPropertyValue(heightProperty)
    panel.style.removeProperty(heightProperty)

    const height = panel.offsetHeight

    /**
     * Reset height to the value calculated before
     */
    panel.style.setProperty(heightProperty, currentHeight)

    /**
     * @note Calculating `this.offsetHeight` does nothing, but allows the
     * browser to re-render, meaning the accordion can expand smoothly
     */
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight

    return height
  }

  handleToggleClick () {
    if (this.isOpen) {
      this.isOpen = false
    } else {
      this.isOpen = true
    }
  }

  /** @param {number} height height in px */
  setPanelHeight (height) {
    const panel = this.els.panel?.element
    if (!panel) return

    panel.style.setProperty('height', `${height}px`)
  }

  get isOpen () {
    return this.hasAttribute('open')
  }

  set isOpen (val) {
    this.toggleAttribute('open', val)

    if (this.dataset.groupId === 'facets') {
      if (val) {
        if (!window.theme.tagalys.openFacets.includes(this.dataset.id)) {
          window.theme.tagalys.openFacets.push(this.dataset.id)
        }
      } else {
        const index = window.theme.tagalys.openFacets.indexOf(this.dataset.id)
        if (index > -1) {
          window.theme.tagalys.openFacets.splice(index, 1)
        }
      }
    }

    this.els.panel?.element?.setAttribute('aria-expanded', val.toString())

    this.setPanelHeight(val ? this.getExpandedHeight() : 0)

    this.dispatchEvent(
      new CustomEvent(val ? EVENTS.ACCORDION_OPEN : EVENTS.ACCORDION_CLOSED, {
        detail: {
          open: this.isOpen,
        },
        bubbles: true,
      }),
    )
  }
}

if (!customElements.get('accordion-drawer')) {
  customElements.define('accordion-drawer', AccordionDrawer)
}

class AccordionGroup extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, EVENTS.ACCORDION_OPEN, event =>
      this.onDrawerOpen(event),
    )
  }

  /** @param {CustomEvent} event */
  onDrawerOpen (event) {
    if (this.disableOnlyOneOpen) return

    this.els.drawer.elements.forEach(drawer => {
      const isTargetDrawer = drawer === event.target

      if (!isTargetDrawer) {
        drawer.isOpen = false
      }
    })
  }

  get disableOnlyOneOpen () {
    return this.dataset.disableOnlyOneOpen === 'true'
  }
}

if (!customElements.get('accordion-group')) {
  customElements.define('accordion-group', AccordionGroup)
}
